import { useState } from "react";
import { X, Sparkle } from "@phosphor-icons/react";
import AIAssistantModal from "./AIAssistantModal";

const GOALS = ["Ipertrofia", "Forza", "Dimagrimento", "Resistenza", "Mobilità"];
const LEVELS = ["Principiante", "Intermedio", "Avanzato"];
const EQUIP_OPTIONS = ["Bilanciere", "Manubri", "Panca", "Cavi", "Macchinari", "Kettlebell", "Elastici", "Corpo libero"];

/**
 * Small on-demand wizard that collects the client's profile and asks Claude
 * to draft a weekly workout program. Coach can then copy the JSON output and
 * paste it into the workout builder — Claude never writes to the DB directly.
 */
export default function AIWorkoutGenDialog({ onClose, clientId }) {
  const [goal, setGoal] = useState("Ipertrofia");
  const [level, setLevel] = useState("Intermedio");
  const [daysPerWeek, setDaysPerWeek] = useState(3);
  const [minutes, setMinutes] = useState(60);
  const [equipment, setEquipment] = useState(["Bilanciere", "Manubri", "Panca"]);
  const [focusAreas, setFocusAreas] = useState("");
  const [injuries, setInjuries] = useState("");
  const [running, setRunning] = useState(null); // payload once Generate clicked

  const toggleEquip = (e) => {
    setEquipment((prev) => prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e]);
  };

  const handleRun = () => {
    setRunning({
      goal,
      level,
      days_per_week: Number(daysPerWeek),
      minutes_per_session: Number(minutes),
      equipment,
      focus_areas: focusAreas.split(",").map((s) => s.trim()).filter(Boolean),
      injuries_or_notes: injuries || null,
      client_id: clientId || null,
    });
  };

  if (running) {
    return (
      <AIAssistantModal
        endpoint="generate-workout"
        payload={running}
        title="Genera scheda"
        onClose={onClose}
        clientId={clientId}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-[75] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      data-testid="ai-workout-gen-dialog"
    >
      <button type="button" aria-label="Chiudi" onClick={onClose} className="absolute inset-0 bg-black/75 backdrop-blur-sm cursor-default" />
      <div className="relative w-full max-w-md max-h-[90vh] bg-[#0F0F0F] border border-[#AF52DE]/40 rounded-2xl flex flex-col overflow-hidden">
        <div className="px-5 py-3.5 border-b border-[#1A1A1A] flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sparkle size={16} weight="fill" className="text-[#AF52DE]" />
            <h3 className="text-base font-bold tracking-tight text-white uppercase" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              Genera scheda con Claude
            </h3>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full bg-[#1A1A1A] hover:bg-[#262626] text-zinc-400 hover:text-white flex items-center justify-center" aria-label="Chiudi">
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1 block">Obiettivo</span>
              <select value={goal} onChange={(e) => setGoal(e.target.value)} className="w-full bg-[#171717] border border-[#262626] rounded-lg px-2.5 py-2 text-white outline-none focus:border-[#AF52DE]" data-testid="ai-workout-goal">
                {GOALS.map((g) => <option key={g}>{g}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1 block">Livello</span>
              <select value={level} onChange={(e) => setLevel(e.target.value)} className="w-full bg-[#171717] border border-[#262626] rounded-lg px-2.5 py-2 text-white outline-none focus:border-[#AF52DE]" data-testid="ai-workout-level">
                {LEVELS.map((l) => <option key={l}>{l}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1 block">Giorni/sett.</span>
              <input type="number" min="1" max="7" value={daysPerWeek} onChange={(e) => setDaysPerWeek(e.target.value)} className="w-full bg-[#171717] border border-[#262626] rounded-lg px-2.5 py-2 text-white outline-none focus:border-[#AF52DE]" data-testid="ai-workout-days" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1 block">Min/sessione</span>
              <input type="number" min="20" max="180" step="5" value={minutes} onChange={(e) => setMinutes(e.target.value)} className="w-full bg-[#171717] border border-[#262626] rounded-lg px-2.5 py-2 text-white outline-none focus:border-[#AF52DE]" data-testid="ai-workout-minutes" />
            </label>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1.5">Attrezzatura</p>
            <div className="flex flex-wrap gap-1.5">
              {EQUIP_OPTIONS.map((e) => (
                <button key={e} type="button" onClick={() => toggleEquip(e)} className={`px-2.5 py-1 rounded-full text-[11px] font-medium border ${equipment.includes(e) ? "bg-[#AF52DE]/20 border-[#AF52DE]/50 text-white" : "bg-[#171717] border-[#262626] text-zinc-500"}`}>
                  {e}
                </button>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1 block">Focus (opzionale, separati da virgola)</span>
            <input type="text" value={focusAreas} onChange={(e) => setFocusAreas(e.target.value)} placeholder="es. schiena, glutei" className="w-full bg-[#171717] border border-[#262626] rounded-lg px-2.5 py-2 text-white placeholder:text-zinc-600 outline-none focus:border-[#AF52DE]" data-testid="ai-workout-focus" />
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1 block">Infortuni / note (opzionale)</span>
            <textarea rows={2} value={injuries} onChange={(e) => setInjuries(e.target.value)} placeholder="es. lombalgia, evita squat sotto parallelo" className="w-full bg-[#171717] border border-[#262626] rounded-lg px-2.5 py-2 text-white placeholder:text-zinc-600 outline-none focus:border-[#AF52DE] resize-y" data-testid="ai-workout-injuries" />
          </label>
        </div>

        <div className="px-5 py-3 border-t border-[#1A1A1A] bg-[#0A0A0A] flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-[#1A1A1A] border border-[#262626] text-zinc-300 hover:text-white text-sm font-medium">Annulla</button>
          <button type="button" onClick={handleRun} className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#AF52DE] to-[#7B4BE6] hover:from-[#C46EEB] hover:to-[#9066EF] text-white text-sm font-bold flex items-center gap-1.5" data-testid="ai-workout-generate">
            <Sparkle size={13} weight="fill" /> Genera
          </button>
        </div>
      </div>
    </div>
  );
}
